// Retest tab — mark confirmed findings as fixed or still-vulnerable.

import { useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Empty } from "@/components/ui/Empty";
import { Input } from "@/components/ui/Input";
import { Loading } from "@/components/ui/Loading";
import { SeverityBadge } from "@/components/ui/SeverityBadge";
import { Stat } from "@/components/ui/Stat";
import {
  useEngagementFindings,
  useRetestFinding,
} from "@/hooks/useEngagementDetail";
import { toast } from "@/stores/toastStore";
import { useDict, fmt } from "@/i18n/LocaleProvider";

export function RetestTab({ engagementId }: { engagementId: string }) {
  const t = useDict().app.retest;
  const { data, isLoading } = useEngagementFindings(engagementId);
  const retest = useRetestFinding(engagementId);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  if (isLoading) return <Loading text={t.loading} />;

  const findings = data ?? [];
  const confirmed = findings.filter(
    (f) =>
      f.status === "confirmed" ||
      f.status === "needs_retest" ||
      f.status === "retest_fail",
  );
  const passed = findings.filter((f) => f.status === "retest_pass");
  const failed = findings.filter((f) => f.status === "retest_fail");

  async function doRetest(
    fid: string,
    status: "retest_pass" | "retest_fail",
  ) {
    setSavingId(fid);
    try {
      await retest.mutateAsync({ findingId: fid, status, notes: notes[fid] });
      toast.success(
        status === "retest_pass" ? t.markedFixed : t.markedVuln,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    }
    setSavingId(null);
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="font-mono text-[11px] text-muted uppercase tracking-[0.05em] mb-3">
          {t.summary}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Stat label={t.toRetest} value={confirmed.length} color="rgb(var(--color-warning))" />
          <Stat label={t.fixedPass} value={passed.length} color="rgb(var(--color-success))" />
          <Stat
            label={t.stillVuln}
            value={failed.length}
            color="rgb(var(--color-danger))"
          />
        </div>
      </Card>

      {confirmed.length === 0 ? (
        <Empty text={t.noConfirmed} />
      ) : (
        <div className="flex flex-col gap-2">
          {confirmed.map((f) => (
            <Card key={f.id}>
              <div className="flex items-center gap-2 mb-2">
                <SeverityBadge
                  severity={
                    (f.severity_final ||
                      f.severity_suggested ||
                      "informational") as string
                  }
                />
                <span className="font-mono text-[13px] text-content flex-1">
                  {f.title}
                </span>
                {f.retest_date && (
                  <Badge color="rgb(var(--color-brand))" bg="rgb(var(--color-brand-bg))">
                    {t.retested}
                  </Badge>
                )}
              </div>
              <div className="font-sans text-[12px] text-muted mb-2">
                {(f.description_draft ?? "").substring(0, 150)}
              </div>
              <Input
                label={t.notesLabel}
                value={notes[f.id] ?? ""}
                onChange={(v) => setNotes((n) => ({ ...n, [f.id]: v }))}
                placeholder={t.notesPlaceholder}
                textarea
              />
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  small
                  disabled={savingId === f.id}
                  onClick={() => doRetest(f.id, "retest_pass")}
                >
                  {savingId === f.id ? t.busy : t.markFixed}
                </Button>
                <Button
                  variant="danger"
                  small
                  disabled={savingId === f.id}
                  onClick={() => doRetest(f.id, "retest_fail")}
                >
                  {savingId === f.id ? t.busy : t.markVuln}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {passed.length > 0 && (
        <Card>
          <div className="font-mono text-[11px] text-success uppercase tracking-[0.05em] mb-3">
            {fmt(t.fixedCount, { n: passed.length })}
          </div>
          {passed.map((f) => (
            <div
              key={f.id}
              className="flex items-center gap-2 py-[6px] border-b border-stroke"
            >
              <SeverityBadge
                severity={
                  (f.severity_final ||
                    f.severity_suggested ||
                    "informational") as string
                }
              />
              <span className="font-mono text-[12px] text-success flex-1">
                {f.title}
              </span>
              <Badge color="rgb(var(--color-success))" bg="rgb(var(--color-success-bg))">
                {t.fixedBadge}
              </Badge>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
